import { Fragment, useContext, useMemo, useRef, useState } from 'react';
import CustomModal from '../Reusable/Modal/CustomModal';
import TableContainer from '../Reusable/Table/TableContainer';
import { tableConfig } from './tableConfig';
import { useModalToggle } from '../../hooks/useModalToggle';
import { useDispatch, useSelector } from 'react-redux';
import { locale, localStorageConstants } from '../../constants/globals';
import LanguageSelect from './LanguageSelect';
import AuthContext from '../../context/auth_context';
import EditSettingsFormContainer from './EditSettingsFormContainer';
import { AdministrationForms } from './utils';
import ActionsSettingsFormContainer from './ActionsForm/ActionsSettingsFormContainer';

const AdministrationTable = (props) => {
  const {
      administrationRulesList,
      t,
      gt,
      page,
      rowsPerPage,
      totalElements,
      setActivePage,
      setPageSize,
      loading,
      clearSettings,
      fetchFilterSettings,
      fetchTableActionsSettings,
      fetchTableSettings,
      updateFilterSettings,
      updateTableSettings,
      updateTableActionsSettings,
    } = props || {},
    {
      AdministrationTable: AdministrationTableTranslations,
      Modals,
      Form,
      ActionsForm,
    } = t || {},
    { tableTitle, HeaderColumns, Tooltips } =
      AdministrationTableTranslations || {};

  const { allowedActions } = useContext(AuthContext);
  const { settings, loadingSettings } = useSelector(
    (state) => state.administrationReducer
  );

  const [showModal, toggleModal, item, toggleUpdate, reference] =
    useModalToggle(false);

  const dispatch = useDispatch();
  const submitRef = useRef();

  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem(localStorageConstants.LOCALE).toUpperCase() ||
      locale.default.toUpperCase()
  );

  const onModalOpenHandler = (item, formKey) => {
    if (item) {
      let formType = '';
      switch (formKey) {
        case AdministrationForms.FILTERS:
          dispatch(fetchFilterSettings(item?.key, currentLanguage));
          formType = AdministrationForms.FILTERS;
          break;
        case AdministrationForms.ACTIONS:
          dispatch(fetchTableActionsSettings(item?.key, currentLanguage));
          formType = AdministrationForms.ACTIONS;
          break;
        default:
          dispatch(fetchTableSettings(item?.key, currentLanguage));
          formType = AdministrationForms.TABLE;
      }
      toggleUpdate({ label: item.label, key: item.key }, formType);
    }
  };

  const modalCloseHandler = () => {
    toggleModal(false);
    dispatch(clearSettings());
  };

  const onSubmitHandler = (settingsPayload) => {
    if (settingsPayload) {
      let action;
      switch (reference) {
        case AdministrationForms.FILTERS:
          action = updateFilterSettings;
          break;
        case AdministrationForms.ACTIONS:
          action = updateTableActionsSettings;
          break;
        default:
          action = updateTableSettings;
      }
      dispatch(
        action(
          settingsPayload,
          Form.NotificationMessages,
          modalCloseHandler,
          item?.label,
          currentLanguage
        )
      );
    }
  };

  const tableConfiguration = tableConfig({
    editAdminFilterAction: (item) =>
      onModalOpenHandler(item, AdministrationForms.FILTERS),
    editAdminTableAction: (item) =>
      onModalOpenHandler(item, AdministrationForms.TABLE),
    editAdminTableActionsAction: (item) =>
      onModalOpenHandler(item, AdministrationForms.ACTIONS),
    allowedActions,
  });

  const modalTitle = useMemo(() => {
    const {
      modalForTableActionsSettingsTitle,
      modalForFilterSettingsTitle,
      modalForTableSettingsTitle,
    } = Modals || {};
    let firstPartOfTitle = '';
    switch (reference) {
      case AdministrationForms.FILTERS:
        firstPartOfTitle = modalForFilterSettingsTitle;
        break;
      case AdministrationForms?.ACTIONS:
        firstPartOfTitle = modalForTableActionsSettingsTitle;
        break;
      default:
        firstPartOfTitle = modalForTableSettingsTitle;
        break;
    }
    return `${firstPartOfTitle}: ${item?.label}`;
  }, [reference, item, Modals]);

  return (
    <Fragment>
      <TableContainer
        tableId="administrationRules"
        items={administrationRulesList}
        renderAction={false}
        gt={gt}
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={totalElements}
        tableConfig={tableConfiguration}
        setActivePage={setActivePage}
        setPageSize={setPageSize}
        loading={loading}
        translations={{ tableTitle, Tooltips, HeaderColumns }}
      />
      {showModal && (
        <CustomModal
          show={showModal}
          hideModal={modalCloseHandler}
          title={modalTitle}
          submitRef={submitRef}
          loading={loadingSettings}
          modalSize={'xl'}
          saveButtonClassName={'admin-btn-save h-36'}
          footerContent={
            <div className={'position-absolute start-0 px-4'}>
              <LanguageSelect
                currentLanguage={currentLanguage}
                setCurrentLanguage={setCurrentLanguage}
              />
            </div>
          }
        >
          {reference === AdministrationForms.ACTIONS ? (
            <ActionsSettingsFormContainer
              settings={settings}
              translations={ActionsForm}
              submitRef={submitRef}
              onSubmit={onSubmitHandler}
            />
          ) : (
            <EditSettingsFormContainer
              settings={settings}
              onSubmit={onSubmitHandler}
              reference={reference}
              formType={AdministrationForms}
              translations={Form}
              submitRef={submitRef}
            />
          )}
        </CustomModal>
      )}
    </Fragment>
  );
};
export default AdministrationTable;
